/**
 * The live preview's transitions — a run, a stop, an added or removed client, a whole test run — chained so two never
 * interleave: the page holds ONE game environment (test-utils' active environment, its ordered message channels and
 * the input focus are page-wide), and a mount must be disposed before the next one mounts.
 */
export interface PreviewQueue {
  /** Runs `transition` once every transition queued before it has settled; resolves or rejects with its outcome. */
  enqueue<T>(transition: () => Promise<T>): Promise<T>;
}

export function createPreviewQueue(): PreviewQueue {
  let settled: Promise<unknown> = Promise.resolve();
  return {
    enqueue<T>(transition: () => Promise<T>): Promise<T> {
      const next = settled.then(transition);
      settled = next.catch(() => undefined);
      return next;
    },
  };
}

/**
 * Runs `work` with the live preview suspended, as ONE transition of `queue`: `suspend` before it, and `resume` with
 * what `suspend` returned after it, whatever its outcome. The caller gets `work`'s result as soon as `work` settles —
 * an AI test request is not held up by the preview booting again; the resume is the tail of the transition, which
 * the next transition (a Run, a Stop, another test run) waits for.
 */
export function runWithPreviewSuspended<S, T>(
  queue: PreviewQueue,
  suspend: () => Promise<S>,
  resume: (suspended: S) => Promise<void>,
  work: () => Promise<T>,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    queue
      .enqueue(async () => {
        let suspended: S;
        try {
          suspended = await suspend();
        } catch (err) {
          reject(err);
          return;
        }
        try {
          resolve(await work());
        } catch (err) {
          reject(err);
        }
        await resume(suspended);
      })
      .catch((err) => {
        // The caller already has its result; a resume that throws is the preview's failure, reported here.
        console.error('[Sandbox] the live preview failed to resume after a test run:', err);
      });
  });
}
